import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Eye, EyeOff, ChevronLeft, ChevronRight } from 'lucide-react'
import { useUserDetail, useSuspendUser, useUpdateUser } from '../../hooks/useUsers'
import { usersAPI, type UserWithStats } from '../../api/users'
import { useDeliveries } from '../../hooks/useDeliveries'
import { useAuthStore } from '../../store/authStore'
import KpiCard from '../../components/ui/KpiCard'
import { ExpediteurStatusBadge, PlanBadge } from './AdminExpediteurs'
import type { Delivery } from '../../types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short', year: 'numeric' })

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente', validated: 'Validé', accepted: 'Accepté',
  picked_up: 'Récupéré', in_transit: 'En route', delivered: 'Livré',
  failed: 'Échoué', cancelled: 'Annulé', unreachable: 'Injoignable',
}
const STATUS_COLOR: Record<string, string> = {
  delivered: '#10B981', pending: '#F59E0B', in_transit: '#3B82F6',
  failed: '#EF4444', cancelled: '#9CA3AF',
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function AvatarBadge({ name, size = 40 }: { name: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: '#E1F5EE', color: '#0F6E56',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.35, flexShrink: 0,
    }}>
      {name.trim().slice(0, 2).toUpperCase()}
    </div>
  )
}

function InfoChip({ icon, value }: { icon: string; value?: string }) {
  if (!value) return null
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#374151' }}>
      <span>{icon}</span>
      <span>{value}</span>
    </span>
  )
}

function SkeletonBlock({ height }: { height: number }) {
  return (
    <div style={{
      height, borderRadius: 10, background: '#F3F4F6',
      animation: 'pulse 1.5s ease-in-out infinite',
    }} />
  )
}

const TABLE_STATUS_CHIPS = [
  { key: '', label: 'Tous' },
  { key: 'pending', label: 'En attente' },
  { key: 'in_transit', label: 'En route' },
  { key: 'delivered', label: 'Livré' },
  { key: 'cancelled', label: 'Annulé' },
  { key: 'failed', label: 'Échoué' },
]

const TABLE_LIMIT = 15

interface ParcelsTableProps {
  deliveries: Delivery[]
  total: number
  totalPages: number
  page: number
  onPageChange: (p: number) => void
  statusFilter: string
  onStatusChange: (s: string) => void
  isLoading: boolean
  onView: (id: string) => void
}

function ParcelsTable({
  deliveries, total, totalPages, page, onPageChange,
  statusFilter, onStatusChange, isLoading, onView,
}: ParcelsTableProps) {
  return (
    <div>
      {/* Status chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {TABLE_STATUS_CHIPS.map((c) => (
          <button
            key={c.key}
            onClick={() => { onStatusChange(c.key); onPageChange(1) }}
            style={{
              padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', border: 'none',
              background: statusFilter === c.key ? '#0D1117' : '#F3F4F6',
              color: statusFilter === c.key ? '#fff' : '#374151',
            }}
          >
            {c.label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9CA3AF', alignSelf: 'center' }}>
          {total} colis
        </span>
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Chargement…</div>
      ) : !deliveries.length ? (
        <div style={{ color: '#9CA3AF', fontSize: 13, padding: '20px 0' }}>Aucun colis</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #F3F4F6' }}>
                {['CODE', 'DESTINATAIRE', 'STATUT', 'MONTANT', 'DATE', 'ACTIONS'].map((h) => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11, letterSpacing: '0.04em' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => {
                const color = STATUS_COLOR[d.status] ?? '#9CA3AF'
                return (
                  <tr key={d._id} style={{ borderBottom: '1px solid #F9FAFB' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#0D1117', fontFamily: 'monospace' }}>{d.trackingCode}</td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>
                      <div style={{ fontWeight: 600 }}>{d.dropoff?.contactName}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{d.dropoff?.address?.slice(0, 35)}</div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: color + '1A', color, borderRadius: 20, padding: '3px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {STATUS_LABELS[d.status] ?? d.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0D1117' }}>{d.price?.base} TND</td>
                    <td style={{ padding: '10px 12px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>{fmtDate(d.createdAt)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <button
                        onClick={() => onView(d._id)}
                        style={{
                          padding: '5px 12px', borderRadius: 7, border: '1px solid #E5E7EB',
                          background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                          color: '#374151',
                        }}
                      >
                        Voir
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            style={{
              padding: '6px 10px', borderRadius: 8, border: '1px solid #E5E7EB',
              background: '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer',
              color: page <= 1 ? '#D1D5DB' : '#374151', display: 'flex', alignItems: 'center',
            }}
          >
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
            .reduce<(number | '...')[]>((acc, p, idx, arr) => {
              if (idx > 0 && (arr[idx - 1] as number) < p - 1) acc.push('...')
              acc.push(p)
              return acc
            }, [])
            .map((p, i) =>
              p === '...' ? (
                <span key={`e-${i}`} style={{ fontSize: 12, color: '#9CA3AF', padding: '0 4px' }}>…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => onPageChange(p as number)}
                  style={{
                    width: 32, height: 32, borderRadius: 8,
                    border: page === p ? 'none' : '1px solid #E5E7EB',
                    background: page === p ? '#0D1117' : '#fff',
                    color: page === p ? '#fff' : '#374151',
                    cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  }}
                >
                  {p}
                </button>
              )
            )}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            style={{
              padding: '6px 10px', borderRadius: 8, border: '1px solid #E5E7EB',
              background: '#fff', cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              color: page >= totalPages ? '#D1D5DB' : '#374151', display: 'flex', alignItems: 'center',
            }}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  user: UserWithStats
}

function EditModal({ isOpen, onClose, onSuccess, user }: EditModalProps) {
  const [form, setForm] = useState({
    name: user.name,
    email: user.email ?? '',
    phone: user.phone,
    companyName: user.company?.name ?? '',
    plan: user.plan ?? 'free',
    isVerified: user.isVerified ?? false,
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const set = (field: string, value: string | boolean) =>
    setForm((f) => ({ ...f, [field]: value }))

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Nom obligatoire'
    if (!form.phone.trim()) e.phone = 'Téléphone obligatoire'
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setLoading(true)
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        company: { name: form.companyName.trim() || undefined },
        plan: form.plan,
        isVerified: form.isVerified,
      }
      if (form.email.trim()) payload.email = form.email.trim()
      await usersAPI.update(user._id, payload)
      toast.success('Profil mis à jour')
      onSuccess()
      onClose()
    } catch {
      toast.error('Erreur lors de la mise à jour')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
    border: '1px solid #E5E7EB', outline: 'none', boxSizing: 'border-box',
  }
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }
  const errorStyle: React.CSSProperties = { fontSize: 11, color: '#EF4444' }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 460, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0D1117', marginBottom: 24 }}>Modifier le profil</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Nom *</label>
            <input style={inputStyle} value={form.name} onChange={(e) => set('name', e.target.value)} />
            {errors.name && <span style={errorStyle}>{errors.name}</span>}
          </div>
          <div>
            <label style={labelStyle}>Téléphone *</label>
            <input style={inputStyle} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            {errors.phone && <span style={errorStyle}>{errors.phone}</span>}
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} value={form.email} onChange={(e) => set('email', e.target.value)} />
            {errors.email && <span style={errorStyle}>{errors.email}</span>}
          </div>
          <div>
            <label style={labelStyle}>Entreprise</label>
            <input style={inputStyle} value={form.companyName} onChange={(e) => set('companyName', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Plan</label>
            <select style={inputStyle} value={form.plan} onChange={(e) => set('plan', e.target.value)}>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="business">Business</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.isVerified} onChange={(e) => set('isVerified', e.target.checked)} />
            <span style={{ fontSize: 13, color: '#374151' }}>Compte vérifié</span>
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={loading} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: loading ? '#9CA3AF' : '#1D9E75', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
            {loading ? 'Enregistrement…' : 'Mettre à jour'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Change Password Modal ────────────────────────────────────────────────────

function ChangePasswordModal({ isOpen, onClose, userId }: { isOpen: boolean; onClose: () => void; userId: string }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
    border: '1px solid #E5E7EB', outline: 'none', boxSizing: 'border-box',
    paddingRight: '40px',
  }
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }

  const handleSubmit = async () => {
    if (password.length < 6) { toast.error('Mot de passe trop court (6 caractères min.)'); return }
    if (password !== confirm) { toast.error('Les mots de passe ne correspondent pas'); return }
    setLoading(true)
    try {
      await usersAPI.changePassword(userId, password)
      toast.success('Mot de passe modifié')
      onClose()
      setPassword('')
      setConfirm('')
    } catch {
      toast.error('Erreur lors du changement de mot de passe')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 400, maxWidth: '90vw' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0D1117', marginBottom: 24 }}>Changer le mot de passe</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Nouveau mot de passe</label>
            <div style={{ position: 'relative' }}>
              <input
                style={inputStyle}
                type={showPwd ? 'text' : 'password'}
                placeholder="Min. 6 caractères"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', alignItems: 'center', padding: 0 }}
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Confirmer le mot de passe</label>
            <div style={{ position: 'relative' }}>
              <input
                style={inputStyle}
                type={showConfirm ? 'text' : 'password'}
                placeholder="Répéter le mot de passe"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit() }}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', alignItems: 'center', padding: 0 }}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: loading ? '#9CA3AF' : '#7C3AED', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            {loading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Frais livraison card ─────────────────────────────────────────────────────

const FRAIS_PRESETS = [3, 5, 7, 10, 12, 15]

function FraisLivraisonCard({
  user,
  cardStyle,
  onSaved,
}: {
  user: UserWithStats
  cardStyle: React.CSSProperties
  onSaved: () => void
}) {
  const [current, setCurrent] = useState<number>(user.fraisLivraison ?? 0)
  const [custom, setCustom]   = useState(false)
  const [customVal, setCustomVal] = useState(String(user.fraisLivraison ?? 0))

  const mut = useMutation({
    mutationFn: (val: number) => usersAPI.setFraisLivraison(user._id, val),
    onSuccess: (_d, val) => {
      setCurrent(val)
      toast.success('Frais de livraison mis à jour')
      onSaved()
    },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  })

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
        Frais de livraison fixe
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 14 }}>
        Montant ajouté au total de chaque colis créé par cet expéditeur
      </div>

      <div style={{ fontSize: 34, fontWeight: 800, color: '#1D9E75', marginBottom: 14 }}>
        {current.toFixed(3)}{' '}
        <span style={{ fontSize: 14, fontWeight: 500, color: '#6B7280' }}>TND / colis</span>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {FRAIS_PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            disabled={mut.isPending}
            onClick={() => { setCustom(false); mut.mutate(p) }}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: mut.isPending ? 'not-allowed' : 'pointer',
              border: `1.5px solid ${current === p ? '#1D9E75' : '#e5e7eb'}`,
              background: current === p ? '#D1FAE5' : '#fff',
              color: current === p ? '#065F46' : '#374151',
            }}
          >
            {p} TND
          </button>
        ))}
        <button
          type="button"
          onClick={() => setCustom((c) => !c)}
          style={{
            padding: '6px 12px', borderRadius: 8, fontSize: 13,
            cursor: 'pointer',
            border: `1.5px solid ${custom ? '#1D9E75' : '#e5e7eb'}`,
            background: custom ? '#D1FAE5' : '#fff',
            color: custom ? '#065F46' : '#6b7280',
          }}
        >
          Personnalisé
        </button>
      </div>

      {custom && (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <input
            type="number" min="0" step="0.5" value={customVal}
            onChange={(e) => setCustomVal(e.target.value)}
            style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, outline: 'none' }}
            placeholder="Ex: 8.5"
          />
          <button
            type="button"
            disabled={mut.isPending}
            onClick={() => {
              const v = parseFloat(customVal)
              if (!isNaN(v) && v >= 0) mut.mutate(v)
            }}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1D9E75', color: '#fff', fontSize: 13, fontWeight: 600, cursor: mut.isPending ? 'not-allowed' : 'pointer' }}
          >
            {mut.isPending ? '…' : 'Sauvegarder'}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminExpediteurDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin'
  const [showEdit, setShowEdit] = useState(false)
  const [showChangePwd, setShowChangePwd] = useState(false)
  const [tableStatus, setTableStatus] = useState('')
  const [tablePage, setTablePage] = useState(1)

  const { data, isLoading } = useUserDetail(id!)
  const { data: parcelsData, isLoading: parcelsLoading } = useDeliveries({
    expediteur: id,
    status: tableStatus || undefined,
    page: tablePage,
    limit: TABLE_LIMIT,
  })

  const suspendUser = useSuspendUser()
  const updateUser = useUpdateUser()

  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 12, padding: '20px 24px',
    border: '1px solid #F3F4F6', marginBottom: 16,
  }

  const btnStyle: React.CSSProperties = {
    padding: '9px 16px', borderRadius: 8, border: 'none',
    cursor: 'pointer', fontSize: 13, fontWeight: 600,
  }

  const handleSuspend = () => {
    if (!id) return
    if (data?.user.isSuspended) {
      suspendUser.mutate({ id, suspended: false })
    } else {
      const reason = window.prompt('Motif de suspension :')
      if (reason !== null) suspendUser.mutate({ id, suspended: true, reason })
    }
  }

  const handleVerify = () => {
    if (!id) return
    updateUser.mutate({ id, data: { isVerified: true } })
  }

  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['user', id] })
    queryClient.invalidateQueries({ queryKey: ['users'] })
  }

  if (isLoading) {
    return (
      <div>
        <button onClick={() => navigate('/admin/expediteurs')} style={{ ...btnStyle, background: 'none', color: '#6B7280', border: '1px solid #E5E7EB', marginBottom: 20 }}>
          ← Retour
        </button>
        <div style={cardStyle}><SkeletonBlock height={100} /></div>
        <div className="stat-grid stat-grid-4" style={{ marginBottom: 16 }}>
          {[1, 2, 3, 4].map((i) => <div key={i} style={cardStyle}><SkeletonBlock height={80} /></div>)}
        </div>
      </div>
    )
  }

  const u = data?.user
  const stats = data?.stats

  return (
    <div>
      {/* Back button */}
      <button
        onClick={() => navigate('/admin/expediteurs')}
        style={{ ...btnStyle, background: 'none', color: '#6B7280', border: '1px solid #E5E7EB', marginBottom: 20 }}
      >
        ← Retour aux expéditeurs
      </button>

      {/* Profile header */}
      <div style={{ ...cardStyle, display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <AvatarBadge name={u?.company?.name ?? u?.name ?? '?'} size={56} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0D1117', margin: 0 }}>
                {u?.company?.name ?? u?.name}
              </h2>
              {u?.company?.name && (
                <div style={{ color: '#6B7280', fontSize: 14, marginTop: 2 }}>{u.name}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <ExpediteurStatusBadge suspended={u?.isSuspended} verified={u?.isVerified} />
              <PlanBadge plan={u?.plan ?? 'free'} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
            <InfoChip icon="📞" value={u?.phone} />
            <InfoChip icon="📧" value={u?.email ?? '—'} />
            <InfoChip icon="📅" value={u?.createdAt ? `Inscrit le ${fmtDate(u.createdAt)}` : undefined} />
          </div>
        </div>
      </div>

      {/* KPI stats */}
      <div className="stat-grid stat-grid-5" style={{ marginBottom: 16 }}>
        <KpiCard label="Total colis" value={stats?.total ?? 0} icon="📦" color="#1D9E75" sub="" />
        <KpiCard label="Livrés" value={stats?.delivered ?? 0} icon="✅" color="#10B981" sub="" />
        <KpiCard label="Annulés" value={stats?.cancelled ?? 0} icon="🚫" color="#9CA3AF" sub="" />
        <KpiCard label="Échoués" value={stats?.failed ?? 0} icon="❌" color="#EF4444" sub="" />
        <KpiCard label="Total dépensé" value={`${stats?.totalSpent ?? 0} TND`} icon="💰" color="#F59E0B" sub="" />
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={() => setShowEdit(true)} style={{ ...btnStyle, background: '#F9FAFB', color: '#374151', border: '1px solid #E5E7EB' }}>
          ✏️ Modifier le profil
        </button>
        <button
          onClick={handleSuspend}
          disabled={suspendUser.isPending}
          style={{ ...btnStyle, background: u?.isSuspended ? '#10B981' : '#EF4444', color: '#fff' }}
        >
          {u?.isSuspended ? '✅ Réactiver le compte' : '🚫 Suspendre le compte'}
        </button>
        {!u?.isVerified && (
          <button
            onClick={handleVerify}
            disabled={updateUser.isPending}
            style={{ ...btnStyle, background: '#3B82F6', color: '#fff' }}
          >
            ✓ Marquer comme vérifié
          </button>
        )}
        {isAdmin && (
          <button onClick={() => setShowChangePwd(true)} style={{ ...btnStyle, background: '#7C3AED', color: '#fff' }}>
            🔑 Changer le mot de passe
          </button>
        )}
      </div>

      {/* Frais livraison */}
      {u && (
        <FraisLivraisonCard
          user={u}
          cardStyle={cardStyle}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['user', id] })}
        />
      )}

      {/* Parcels table */}
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0D1117', marginBottom: 14 }}>
          Colis de cet expéditeur
        </div>
        <ParcelsTable
          deliveries={parcelsData?.data ?? []}
          total={parcelsData?.total ?? 0}
          totalPages={parcelsData?.totalPages ?? 1}
          page={tablePage}
          onPageChange={setTablePage}
          statusFilter={tableStatus}
          onStatusChange={(s) => { setTableStatus(s); setTablePage(1) }}
          isLoading={parcelsLoading}
          onView={(deliveryId) => navigate(`/admin/livraisons/${deliveryId}`)}
        />
      </div>

      {/* Edit modal */}
      {u && (
        <EditModal
          isOpen={showEdit}
          user={u}
          onClose={() => setShowEdit(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Change password modal — admin only */}
      {isAdmin && id && (
        <ChangePasswordModal
          isOpen={showChangePwd}
          onClose={() => setShowChangePwd(false)}
          userId={id}
        />
      )}
    </div>
  )
}
